import React, { useState } from "react";
import { useData, save } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, DataTable, Button, Modal, Field, Input, Textarea, Select, StatusBadge, SizeEditor } from "../components/shared";
import { Plus, Trash2 } from "lucide-react";
import { money, num, fmtDate } from "../lib/api";

const STATUS = ["Ordered", "Cutting", "In Production", "With Jobber", "Checking", "Finishing", "Finished", "Ready for Dispatch", "Dispatched"];

export default function SalesOrders() {
  const { data, loading, reload } = useData("/sales-orders");
  const { data: customers } = useData("/customers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);

  const blank = { customer_id: "", customer_name: "", customer_po: "", order_date: new Date().toISOString().slice(0, 10), delivery_date: "", design_number: "", item_type: "", colour: "", sizes: [{ size: "", qty: 0 }], rate: "", status: "Ordered", remarks: "" };
  const openForm = (r) => { setForm(r ? { ...r } : blank); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const cust = (customers || []).find((c) => c.id === form.customer_id);
    const body = { ...form, customer_name: cust?.name || form.customer_name, sizes: form.sizes.filter((s) => s.size) };
    await save(form.id ? "put" : "post", form.id ? `/sales-orders/${form.id}` : "/sales-orders", body);
    toast.success("Sales order saved"); setOpen(false); reload();
  };

  const delSO = async (r) => {
    if (!window.confirm(`Delete sales order ${r.code}?`)) return;
    try { await api.delete(`/sales-orders/${r.id}`); toast.success("Sales order deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const columns = [
    { key: "code", header: "SO Number", mono: true, render: (r) => <span className="font-semibold text-violet-700">{r.code}</span> },
    { key: "order_date", header: "Date", render: (r) => fmtDate(r.order_date) },
    { key: "customer_name", header: "Customer" },
    { key: "design_number", header: "Design" },
    { key: "colour", header: "Colour" },
    { key: "total_qty", header: "Qty", align: "right", render: (r) => num(r.total_qty) },
    { key: "rate", header: "Rate", align: "right", render: (r) => num(r.rate) },
    { key: "amount", header: "Amount", align: "right", render: (r) => money(r.amount) },
    { key: "delivery_date", header: "Delivery", render: (r) => fmtDate(r.delivery_date) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", header: "", sortable: false, render: (r) => (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" variant="ghost" onClick={() => openForm(r)}>Edit</Button>
        <button title="Delete" onClick={() => delSO(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`so-delete-${r.id}`}><Trash2 size={15} /></button>
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Sales Orders" subtitle="Buyer orders linked to production status">
        <Button onClick={() => openForm(null)} data-testid="add-so-btn"><Plus size={15} /> New Sales Order</Button>
      </PageHeader>
      {!loading && <DataTable testid="so-table" columns={columns} rows={data} exportName="sales-orders.csv" onRowClick={(r) => openForm(r)} />}

      <Modal open={open} onClose={() => setOpen(false)} title={form?.id ? "Edit Sales Order" : "New Sales Order"} wide>
        {form && (
          <form onSubmit={submit} className="grid grid-cols-3 gap-4">
            <Field label="Customer" required>
              <Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} required placeholder="Select customer"
                options={(customers || []).map((c) => ({ value: c.id, label: c.name }))} data-testid="so-customer" />
            </Field>
            <Field label="Customer PO"><Input value={form.customer_po} onChange={(e) => setForm({ ...form, customer_po: e.target.value })} /></Field>
            <Field label="Order Date"><Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></Field>
            <Field label="Design Number" required><Input value={form.design_number} onChange={(e) => setForm({ ...form, design_number: e.target.value })} required data-testid="so-design" /></Field>
            <Field label="Item Type"><Input value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} /></Field>
            <Field label="Colour"><Input value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} /></Field>
            <Field label="Rate"><Input type="number" step="any" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></Field>
            <Field label="Delivery Date"><Input type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></Field>
            <Field label="Status"><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUS} /></Field>
            <Field label="Size-wise Quantity" className="col-span-3"><SizeEditor testid="so-sizes" sizes={form.sizes} onChange={(s) => setForm({ ...form, sizes: s })} /></Field>
            <Field label="Remarks" className="col-span-3"><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
            <div className="col-span-3 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="so-save">Save Order</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
